import axios, {AxiosResponse} from "axios";
import {Registration, RegistrationData} from "@/model/Registration";
import {PagedResult} from "@/service/PagedResult";
import {EmploymentType, RegistrationType} from "@/enums";

const PATH = "/registrations";
const PATH_V3 = "/v3/registrations";

/**
 * A registration as the office sees it when it creates an employee from it. `finishedAt` is missing while the
 * applicant has not finished the registration, `applicant` until they have entered their personal data, and its
 * `email` until they have entered their contact data.
 */
export interface RegistrationV3Dto {
  id: string;
  type: RegistrationType;
  finishedAt?: string;
  employmentType: EmploymentType;
  applicant?: {
    firstName: string;
    lastName: string;
    birthdate: string;
    email?: string;
  };
}

export default {
  /**
   * Returns the registration with the given id, finished or not, with the data of its applicant.
   */
  getRegistrationV3 (id: string): Promise<RegistrationV3Dto> {
    return axios.get(`${PATH_V3}/${id}`)
      .then(response => response.data);
  },
  startRegistration (code: string): Promise<AxiosResponse> {
    return axios.post(`${PATH}`, {
      registrationCode: code
    });
  },
  finishRegistration (uuid: string, registration: RegistrationData) {
    return axios.post(`${PATH}/${uuid}/finish`, registration);
  },
  downloadFile (uuid: string, fileKey: string) {
    return axios.get(`${PATH}/${uuid}/files/${fileKey}`, {
      responseType: "blob"
    });
  },
  getRegistrations (): Promise<AxiosResponse<PagedResult>> {
    return axios.get(`${PATH}`);
  },
  getRegistration (uuid: string): Promise<AxiosResponse<Registration>> {
    return axios.get(`${PATH}/${uuid}`);
  },
  deleteRegistration (uuid: string): Promise<AxiosResponse> {
    return axios.delete(`${PATH}/${uuid}`);
  },
  updateRegistration (uuid: string, registration: RegistrationData) {
    return axios.patch(`${PATH}/${uuid}`, registration, {
      headers: {
        "Content-Type": "application/merge-patch+json"
      }
    });
  },
  getFiles (uuid: string, context: string): Promise<AxiosResponse<any>> {
    return axios.get(`${PATH}/${uuid}/files`, {
      params: {
        context: context
      }
    });
  },
  uploadFile (uuid: string, file: File, context: string) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("context", context.toUpperCase());

    return axios.post(`${PATH}/${uuid}/files`, formData, {
      headers: {
        "Content-Type": "multipart/form-data"
      }
    });
  },
  deleteFile (uuid: string, context: string, fileName: string) {
    return axios.delete(`${PATH}/${uuid}/files/${context}/${fileName}`);
  }
};
